import { useState } from "react";
import { track } from "../analytics";
import "./Contact.css";

const SUPPORT_EMAIL = "support@magicapps.co.in";

/**
 * Web3Forms relays the message to SUPPORT_EMAIL.
 *
 * This key is a public form identifier, not a secret — it is designed to sit in
 * client-side code and grants nothing beyond "deliver a message to the address
 * this key was registered with". It lives in .env only so it is easy to rotate.
 */
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "General question", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /**
   * Posts the message in the background. Nothing opens and the page does not
   * navigate — the visitor stays here and sees the result inline.
   *
   * The previous implementation redirected to a `mailto:` URL and then showed
   * the success panel unconditionally, so a visitor with no mail client (most
   * of them) saw "Thanks for reaching out!" while nothing had been sent.
   * Success is now shown only when the message genuinely went through.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    // The form is noValidate, so these are checked here rather than by the browser.
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("That email address doesn't look right — please check it.");
      return;
    }
    if (!WEB3FORMS_KEY) {
      setError(`Sorry, the form isn't available right now. Please email us at ${SUPPORT_EMAIL}.`);
      return;
    }

    setError("");
    setSending(true);
    track("btnPress_contact_form", { subject: form.subject });

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `EPS Viewer — ${form.subject}`,
          from_name: "EPS Viewer contact form",
          replyto: form.email.trim(),
          name: form.name.trim(),
          email: form.email.trim(),
          enquiry_type: form.subject,
          message: form.message.trim(),
          botcheck: "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || "Send failed");

      track("contact_form_sent", { subject: form.subject });
      setForm({ name: "", email: "", subject: "General question", message: "" });
      setSubmitted(true);
    } catch {
      track("contact_form_failed", { subject: form.subject });
      setError(`Couldn't send your message. Please email us directly at ${SUPPORT_EMAIL}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section">
      <div className="container contact">
        <span className="eyebrow">Get in touch</span>
        <h1 className="section-title">Contact Us</h1>
        <p className="section-lead">
          Have a question, found a bug, or want to suggest a feature for EPS
          Viewer? Fill in the form below — we usually reply within 1–2 business days.
        </p>

        <div className="contact__layout">
          <div className="contact__form-wrap card">
            {submitted ? (
              <div className="contact__success">
                <h3>Thanks for reaching out!</h3>
                <p>
                  Your message has been sent to our team. We reply to most enquiries within
                  1–2 business days — keep an eye on the inbox you gave us, and check your
                  spam folder if you don't hear back.
                </p>
                <button className="btn btn--secondary" onClick={() => setSubmitted(false)}>Send another message</button>
              </div>
            ) : (
              <form className="contact__form" onSubmit={handleSubmit} noValidate>
                <div className="contact__row">
                  <div className="contact__field">
                    <label htmlFor="name">Your name <span className="contact__required">*</span></label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Vaibhav Singhal"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="contact__field">
                    <label htmlFor="email">Your email <span className="contact__required">*</span></label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="contact__field">
                  <label htmlFor="subject">Subject</label>
                  <select id="subject" name="subject" value={form.subject} onChange={handleChange}>
                    <option>General question</option>
                    <option>Bug report</option>
                    <option>Feature request</option>
                    <option>Business enquiry</option>
                    <option>Other</option>
                  </select>
                </div>

                <div className="contact__field">
                  <label htmlFor="message">Message <span className="contact__required">*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    placeholder="Describe your question or issue..."
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Honeypot: hidden from people, tempting to bots. */}
                <input
                  type="checkbox"
                  name="botcheck"
                  className="contact__botcheck"
                  tabIndex={-1}
                  autoComplete="off"
                />

                {error && <p className="contact__error" role="alert">{error}</p>}

                <button
                  type="submit"
                  className="btn btn--primary contact__submit"
                  disabled={sending}
                >
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>

          <div className="contact__sidebar">
            <div className="card contact__card">
              <h3>Email us directly</h3>
              <p>Prefer to write your own email? Reach us at:</p>
              <a className="contact__email-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              <p className="contact__muted">We respond within 1–2 business days.</p>
            </div>

            <div className="card contact__card">
              <h3>Reporting a problem?</h3>
              <p>
                Conversion issues are much faster to fix when we can reproduce them. If you can,
                tell us:
              </p>
              <ul className="contact__list">
                <li>What you expected to see, and what happened instead.</li>
                <li>Your browser and operating system — for example Chrome on Windows 11, or Safari on iPhone.</li>
                <li>Roughly how large the EPS file is, and which application produced it (Illustrator, CorelDRAW, a stock library, and so on).</li>
                <li>Any message shown on screen, copied exactly.</li>
              </ul>
              <p className="contact__muted">
                Please don't attach confidential artwork. If a specific file fails, describe it
                rather than sending it — we can usually reproduce the fault from the description.
              </p>
            </div>

            <div className="card contact__card">
              <h3>What we can't help with</h3>
              <p>
                We can't recover corrupted source files, provide Adobe Illustrator licences or
                support, or convert formats the tool doesn't list. For those,
                our <a href="/guides/">guides</a> and <a href="/faq/">FAQ</a> are usually a
                faster answer than email.
              </p>
            </div>

            <div className="card contact__card">
              <h3>About EPS Viewer</h3>
              <p>Built and maintained by MagicApps.</p>
              <a href="https://magicapps.co.in/" target="_blank" rel="noopener noreferrer">magicapps.co.in →</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
